/** Common on-chain explorer display rules (amounts, addresses, epochs). */

import { tryFormatCpcAddress } from './cpcAddress';

const MIST_PER_DGC = 1_000_000_000n;
const MIST_PER_DATA = 1_000_000_000n;

export function mistToBigInt(mist: string | number | bigint | undefined | null): bigint {
	if (mist === undefined || mist === null || mist === '') return 0n;
	try {
		return BigInt(mist);
	} catch {
		return 0n;
	}
}

/** Human CPC from MIST — trim trailing zeros, max 4 decimal places for UI. */
export function formatMistAsDgc(
	mist: string | number | bigint | undefined | null,
	opts?: { maxDecimals?: number; compact?: boolean },
): string {
	const maxDecimals = opts?.maxDecimals ?? 4;
	const m = mistToBigInt(mist);
	if (m === 0n) return '0';

	if (opts?.compact) {
		return formatCompactCoinAmount(m, MIST_PER_DGC);
	}

	const whole = m / MIST_PER_DGC;
	const frac = m % MIST_PER_DGC;
	if (frac === 0n) return whole.toLocaleString();

	let fracStr = frac.toString().padStart(9, '0').replace(/0+$/, '');
	if (fracStr.length > maxDecimals) {
		fracStr = fracStr.slice(0, maxDecimals).replace(/0+$/, '');
	}
	return fracStr ? `${whole.toLocaleString()}.${fracStr}` : whole.toLocaleString();
}

/** Human DATA from MIST (9 decimals). */
export function formatMistAsData(
	mist: string | number | bigint | undefined | null,
	opts?: { maxDecimals?: number; compact?: boolean },
): string {
	const maxDecimals = opts?.maxDecimals ?? 4;
	const m = mistToBigInt(mist);
	if (m === 0n) return '0';

	if (opts?.compact) {
		return formatCompactCoinAmount(m, MIST_PER_DATA);
	}

	const whole = m / MIST_PER_DATA;
	const frac = m % MIST_PER_DATA;
	if (frac === 0n) return whole.toLocaleString();

	let fracStr = frac.toString().padStart(9, '0').replace(/0+$/, '');
	if (fracStr.length > maxDecimals) {
		fracStr = fracStr.slice(0, maxDecimals).replace(/0+$/, '');
	}
	return fracStr ? `${whole.toLocaleString()}.${fracStr}` : whole.toLocaleString();
}

/** Explorer-style compact: 1.2K / 3.4M / 5.6B */
export function formatCompactAmount(mist: bigint): string {
	return formatCompactCoinAmount(mist, MIST_PER_DGC);
}

function formatCompactCoinAmount(mist: bigint, unit: bigint): string {
	const amount = Number(mist) / Number(unit);
	if (!Number.isFinite(amount) || amount === 0) return '0';
	const abs = Math.abs(amount);
	if (abs >= 1e9) return `${(amount / 1e9).toFixed(2)}B`;
	if (abs >= 1e6) return `${(amount / 1e6).toFixed(2)}M`;
	if (abs >= 1e3) return `${(amount / 1e3).toFixed(2)}K`;
	if (abs >= 100) return amount.toFixed(2);
	if (abs >= 1) return amount.toFixed(4);
	return amount.toFixed(6);
}

export function formatPercent(value: number | null | undefined, digits = 2): string {
	if (value == null || !Number.isFinite(value)) return '—';
	return `${(value * 100).toFixed(digits)}%`;
}

/** `useGetValidatorsApy` 返回的已是百分数（5.2 = 5.2% 年化） */
export function formatAnnualPercent(value: number | null | undefined, digits = 2): string {
	if (value == null || !Number.isFinite(value)) return '—';
	return `${value.toFixed(digits)}%`;
}

/** 年化百分数 → 简单日化百分数（年化 ÷ 365） */
export function annualPercentToDailyPercent(annualPercent: number): number {
	return annualPercent / 365;
}

export function formatDailyYieldPercent(
	annualPercent: number | null | undefined,
	opts?: { unavailable?: boolean; digits?: number; estimated?: boolean },
): string {
	if (opts?.unavailable || annualPercent == null || !Number.isFinite(annualPercent)) return '—';
	if (annualPercent <= 0) return '0%';
	const daily = annualPercentToDailyPercent(annualPercent);
	const digits = opts?.digits ?? (daily < 0.01 ? 4 : 3);
	const prefix = opts?.estimated ? '~' : '';
	return `${prefix}${daily.toFixed(digits)}%`;
}

export function shortenAddress(
	addr: string,
	head = 8,
	tail = 6,
	ellipsis = '..',
): string {
	const a = tryFormatCpcAddress(addr.trim());
	if (a.length <= head + tail + ellipsis.length) return a;
	return `${a.slice(0, head)}${ellipsis}${a.slice(-tail)}`;
}

/** Epoch progress 0–100 from checkpoint index within epoch (approx). */
export function epochProgressPercent(
	checkpointInEpoch: number | null | undefined,
	epochLength: number | null | undefined,
): number | null {
	if (checkpointInEpoch == null || epochLength == null || epochLength <= 0) return null;
	return Math.min(100, Math.max(0, (checkpointInEpoch / epochLength) * 100));
}

export function formatEpochCountdown(ms: number | null | undefined): string {
	if (ms == null || ms <= 0) return '—';
	const sec = Math.floor(ms / 1000);
	const h = Math.floor(sec / 3600);
	const m = Math.floor((sec % 3600) / 60);
	if (h > 0) return `${h}h ${m}m`;
	if (m > 0) return `${m}m`;
	return `${sec}s`;
}
